import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Badge, Button, Input, Select, HelpTooltip } from '@fiado/react-kit';
import { Label } from '../../../../components/ui/label';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { Textarea } from '../../../../components/ui/textarea';
import type {
    ApplicationPermissionDraft,
    LevelDefLocal,
} from '../../../../api/types/ApplicationLocal';
import type { Scope } from '../../../../api/types/permissions';
import { permissionCategoryLabel } from '../../../../lib/permissionCategory';
import { WHOLE_ORGANIZATION } from '../../../../lib/labelConstants';
import { groupByAction } from '../../../../lib/permissionOrder';

/**
 * Scopes base ofrecibles a un permiso CUSTOM, con label legible (anti-id: nunca el enum crudo a la
 * vista). Solo TENANT es base (aplica a toda la organización). Los scopes específicos son los NIVELES
 * que la app definió en el paso "Niveles" (LEVEL_1..3, con su displayName). DEC-RBAC-089: los scopes
 * legacy de dominio (RETAILER/STORE/GROUP) ya NO se ofrecen — el modelo es genérico por nivel.
 */
const BASE_SCOPE_LABELS: Partial<Record<Scope, string>> = {
    TENANT: WHOLE_ORGANIZATION,
};

const LEVEL_SCOPES = ['LEVEL_1', 'LEVEL_2', 'LEVEL_3'] as const;

function scopeOptions(levels: LevelDefLocal[]): { value: string; label: string }[] {
    const base = (Object.entries(BASE_SCOPE_LABELS) as [Scope, string][]).map(([value, label]) => ({
        value,
        label,
    }));
    const levelOpts = levels
        .filter((l) => (LEVEL_SCOPES as readonly string[]).includes(l.level))
        .map((l) => ({ value: l.level, label: l.displayName }));
    // Orden alfabético por label (default del proyecto).
    return [...base, ...levelOpts].sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

interface Props {
    /** Catálogo controlado de permisos de la app (SYSTEM + CUSTOM). */
    value: ApplicationPermissionDraft[];
    /** Se llama con la lista completa actualizada al agregar/editar/quitar un CUSTOM. */
    onChange: (next: ApplicationPermissionDraft[]) => void;
    /** Niveles de la app (para etiquetar los scopes de nivel con su displayName). */
    levels?: LevelDefLocal[];
}

const EMPTY_DRAFT: ApplicationPermissionDraft = {
    permissionKey: '',
    displayName: '',
    description: '',
    scope: 'TENANT',
    kind: 'CUSTOM',
};

/** Etiqueta de categoría de un permiso: la del catálogo o «Otros permisos». */
function categoryLabelOf(perm: ApplicationPermissionDraft): string {
    return permissionCategoryLabel(perm.category);
}

/** Agrupa el catálogo por categoría (dominio) → sub-grupo de acción (reusa `groupByAction` del
 *  PermissionPicker), para presentarlo igual que el selector de permisos. */
function groupCatalog(perms: ApplicationPermissionDraft[]) {
    const byCat = new Map<string, (ApplicationPermissionDraft & { key: string })[]>();
    for (const p of perms) {
        const cat = categoryLabelOf(p);
        const list = byCat.get(cat) ?? [];
        list.push({ ...p, key: p.permissionKey });
        byCat.set(cat, list);
    }
    return [...byCat.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], 'es'))
        .map(([category, items]) => ({ category, groups: groupByAction(items) }));
}

/**
 * Paso CONTROLADO y PURO del catálogo de permisos del armador de aplicaciones (#3-a). No llama a la
 * API: el padre decide si `value` es un buffer en memoria (crear) o el live del backend (editar). Los
 * permisos SYSTEM se muestran read-only con badge "Sistema"; los CUSTOM son editables (agregar / editar
 * / quitar). El dato principal a la vista es el `displayName`, no la key cruda (regla anti-id en UI).
 */
export function PermissionCatalogStep({ value, onChange, levels = [] }: Props) {
    // Editor inline: null = cerrado; string = editando esa key; '' = agregando uno nuevo.
    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState<ApplicationPermissionDraft>(EMPTY_DRAFT);
    const [keyError, setKeyError] = useState<string | undefined>(undefined);

    const scopeOpts = scopeOptions(levels);
    // Agrupado por categoría (dominio) → acción, igual que el PermissionPicker.
    const categories = groupCatalog(value);

    const openAdd = (): void => {
        setDraft(EMPTY_DRAFT);
        setKeyError(undefined);
        setEditing('');
    };
    const openEdit = (perm: ApplicationPermissionDraft): void => {
        setDraft(perm);
        setKeyError(undefined);
        setEditing(perm.permissionKey);
    };
    const cancel = (): void => {
        setEditing(null);
        setKeyError(undefined);
    };

    const save = (): void => {
        const key = draft.permissionKey.trim();
        if (!key) {
            setKeyError('Escribe una clave para el permiso.');
            return;
        }
        const isNew = editing === '';
        // Colisión de key contra OTROS permisos (no contra el que estás editando).
        const collides = value.some((p) => p.permissionKey === key && p.permissionKey !== editing);
        if (isNew && value.some((p) => p.permissionKey === key)) {
            setKeyError('Ya existe un permiso con esa clave.');
            return;
        }
        if (!isNew && collides) {
            setKeyError('Ya existe un permiso con esa clave.');
            return;
        }
        const next: ApplicationPermissionDraft = {
            ...draft,
            permissionKey: key,
            displayName: draft.displayName.trim(),
            description: draft.description.trim(),
            kind: 'CUSTOM',
        };
        onChange(
            isNew ? [...value, next] : value.map((p) => (p.permissionKey === editing ? next : p)),
        );
        setEditing(null);
    };

    const remove = (key: string): void => {
        onChange(value.filter((p) => p.permissionKey !== key));
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                    <Label>Catálogo de permisos</Label>
                    <HelpHint text={HELP_TEXT.permissionCatalog} />
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={openAdd}
                >
                    Agregar permiso
                </Button>
            </div>

            {value.length === 0 && editing === null ? (
                <p className="text-xs text-muted-foreground">
                    Sin permisos propios. Los permisos del sistema vienen de la plantilla; agrega
                    los propios que tu aplicación necesite.
                </p>
            ) : (
                <div className="space-y-5">
                    {categories.map(({ category, groups }) => (
                        <section key={category} className="space-y-3">
                            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                                {category}
                            </h3>
                            {groups.map(({ group, items }) => (
                                <div
                                    key={group.id}
                                    className="space-y-2 border-l border-border pl-3"
                                >
                                    <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
                                        {group.label}
                                    </h4>
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        {items.map((perm) => {
                                            const isSystem = perm.kind === 'SYSTEM';
                                            return (
                                                <div
                                                    key={perm.permissionKey}
                                                    data-testid={`perm-row-${perm.permissionKey}`}
                                                    className="flex items-start justify-between gap-2 rounded-lg border border-border px-3 py-2"
                                                >
                                                    <div className="min-w-0 space-y-0.5">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span
                                                                data-testid="perm-name"
                                                                className="text-sm font-medium leading-tight"
                                                            >
                                                                {perm.displayName ||
                                                                    perm.permissionKey}
                                                            </span>
                                                            {isSystem ? (
                                                                <Badge variant="info">
                                                                    Sistema
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="default">
                                                                    Propio
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        {perm.description && (
                                                            <p className="text-xs text-muted-foreground leading-tight">
                                                                {perm.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {!isSystem && (
                                                        <div className="flex shrink-0 items-center gap-1">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                aria-label={`Editar permiso ${perm.displayName || perm.permissionKey}`}
                                                                onClick={() => {
                                                                    openEdit(perm);
                                                                }}
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                aria-label={`Quitar permiso ${perm.displayName || perm.permissionKey}`}
                                                                onClick={() => {
                                                                    remove(perm.permissionKey);
                                                                }}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </section>
                    ))}
                </div>
            )}

            {editing !== null && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={cancel}
                    />
                    <div className="relative w-full max-w-lg space-y-3 rounded-lg border bg-background p-6 shadow-lg">
                        <h2 className="text-lg font-semibold">
                            {editing === '' ? 'Agregar permiso' : 'Editar permiso'}
                        </h2>
                        <div className="space-y-1">
                            <div className="flex items-center gap-1">
                                <Label htmlFor="pcs-key">Clave</Label>
                                <HelpTooltip content="Clave técnica del permiso (ej. network.agent.invite). No se puede cambiar una vez creado." />
                            </div>
                            <Input
                                id="pcs-key"
                                hint="Identificador único del permiso. Ej: network.agent.invite"
                                value={draft.permissionKey}
                                error={keyError}
                                onChange={(e) => {
                                    setDraft((d) => ({ ...d, permissionKey: e.target.value }));
                                    setKeyError(undefined);
                                }}
                            />
                        </div>
                        <Input
                            id="pcs-name"
                            label="Nombre visible"
                            hint="Cómo se ve en la consola. Ej: Invitar agente"
                            value={draft.displayName}
                            onChange={(e) => {
                                setDraft((d) => ({ ...d, displayName: e.target.value }));
                            }}
                        />
                        <div className="space-y-1">
                            <Label htmlFor="pcs-desc">Descripción</Label>
                            <Textarea
                                id="pcs-desc"
                                value={draft.description}
                                onChange={(e) => {
                                    setDraft((d) => ({ ...d, description: e.target.value }));
                                }}
                            />
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-1">
                                <Label>Alcance</Label>
                                <HelpTooltip content="Toda la organización o uno de los niveles que definiste para esta aplicación (paso Niveles)." />
                            </div>
                            <Select
                                value={draft.scope}
                                options={scopeOpts}
                                onChange={(v) => {
                                    setDraft((d) => ({ ...d, scope: v as Scope }));
                                }}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={cancel}>
                                Cancelar
                            </Button>
                            <Button type="button" size="sm" onClick={save}>
                                Guardar permiso
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
